/**
 * Serialises the simple editor's rows into a DSL expression.
 *
 * ONE DIRECTION ONLY. There is no parser here and there must not be: the server
 * owns the one real parser, and a second implementation on the website is exactly
 * the divergence this whole design set out to delete. Reading an existing rule
 * back into rows is done from the stored AST — see astToRows.js.
 */

export const AGGREGATIONS = [
  { value: 'threshold', label: 'Value', windowed: false },
  { value: 'avg', label: 'Average over', windowed: true },
  { value: 'min', label: 'Lowest in', windowed: true },
  { value: 'max', label: 'Highest in', windowed: true },
  { value: 'slope', label: 'Rate of change over', windowed: true },
  { value: 'aqi', label: 'AQI over', windowed: true },
];

export const COMPARATORS = [
  { value: '>', label: 'is above' },
  { value: '>=', label: 'is at or above' },
  { value: '<', label: 'is below' },
  { value: '<=', label: 'is at or below' },
  { value: '==', label: 'is exactly' },
  { value: '!=', label: 'is anything but' },
];

/** Channels aqi() is defined for. Mirrors rules-spec/aqi-breakpoints.json. */
export const AQI_CHANNELS = ['pm2_5', 'pm10_0', 'co'];

export const emptyConditionRow = () => ({
  channel: '',
  aggregation: 'threshold',
  window: { amount: 10, unit: 'm' },
  comparator: '>',
  value: '',
});

export const emptyBandRow = (label = '') => ({ label, comparator: '<', value: '' });

/** A channel token needs quoting unless it is a bare identifier. */
export const formatChannel = (channel) =>
  /^[A-Za-z][A-Za-z0-9_]*$/.test(channel || '') ? channel : `"${channel}"`;

export const formatDuration = ({ amount, unit }) => `${amount}${unit}`;

const formatTerm = (row) => {
  const channel = formatChannel(row.channel);
  if (row.aggregation === 'threshold') return channel;
  return `${row.aggregation}(${channel}, ${formatDuration(row.window)})`;
};

const trailingClauses = (config) => {
  let out = '';
  if (config.sustain && Number(config.sustain.amount) > 0) {
    out += ` for ${formatDuration(config.sustain)}`;
  }
  // Only emitted when it differs from the server default, so an untouched rule
  // does not carry a clause the user never asked for.
  if (config.settle && Number(config.settle.amount) > 0) {
    const ms = toMillis(config.settle);
    if (ms !== 5000) out += ` settle ${formatDuration(config.settle)}`;
  }
  return out;
};

export const toMillis = ({ amount, unit }) =>
  Number(amount) * { s: 1000, m: 60000, h: 3600000 }[unit];

export const fromMillis = (ms) => {
  if (ms % 3600000 === 0) return { amount: ms / 3600000, unit: 'h' };
  if (ms % 60000 === 0) return { amount: ms / 60000, unit: 'm' };
  return { amount: ms / 1000, unit: 's' };
};

/**
 * Builds the expression for a simple-mode configuration.
 *
 * @param {object} config
 *   mode      'conditions' | 'bands'
 *   joiner    'and' | 'or'  (conditions mode; mixing them is not offered here —
 *             the language rejects it, and formula mode is where that lives)
 *   rows      condition rows, or band rows with the last one implicitly the catch-all
 *   term      the term bands are graded on (bands mode)
 *   sustain / settle  optional {amount, unit}
 */
export const buildExpression = (config) => {
  if (config.mode === 'bands') return buildBandExpression(config);
  return buildConditionExpression(config);
};

const buildConditionExpression = (config) => {
  const parts = (config.rows || [])
    .filter((r) => r.channel && r.value !== '' && r.value !== null && r.value !== undefined)
    .map((r) => `${formatTerm(r)} ${r.comparator} ${r.value}`);
  if (parts.length === 0) return '';
  return `${parts.join(` ${config.joiner || 'and'} `)}${trailingClauses(config)}`;
};

const buildBandExpression = (config) => {
  const rows = config.rows || [];
  // The final row is always the catch-all: without it a reading above the last
  // bound would have no band at all, which the language rejects.
  const bounded = rows.slice(0, -1).filter((r) => r.label && r.value !== '' && r.value !== null);
  const last = rows[rows.length - 1];
  if (bounded.length === 0 || !last || !last.label || !config.term?.channel) return '';

  const cases = bounded.map((r) => `${formatLabel(r.label)}: ${r.comparator}${r.value}`);
  cases.push(`${formatLabel(last.label)}: *`);
  return `band ${formatTerm(config.term)} { ${cases.join(', ')} }${trailingClauses(config)}`;
};

/** Band labels are free-form display text, so anything non-bare gets quoted. */
export const formatLabel = (label) =>
  /^[A-Za-z][A-Za-z0-9_]*$/.test(label || '') ? label : `"${label}"`;
